import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { AlertTriangle, Info } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { updateDomain, updateDisplayName } from '@/api/auth'
import { ApiError } from '@/api/client'
import { MOCK_BASE_DOMAIN } from '@/lib/constants'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toast } from 'sonner'

const RESERVED_DOMAINS = new Set([
  'app', 'www', 'api', 'admin', 'mail', 'support', 'help', 'blog', 'docs',
  'status', 'about', 'terms', 'privacy', 'login', 'signup', 'dashboard',
  'settings', 'billing', 'account', 'user', 'users', 'project', 'projects',
  'mock', 'mocks', 'endpoint', 'endpoints', 'scenario', 'scenarios',
  'response', 'responses', 'log', 'logs', 'webhook', 'webhooks',
])

const domainSchema = z.object({
  domain: z
    .string()
    .min(3, 'Domain must be at least 3 characters')
    .max(20, 'Domain must be at most 20 characters')
    .regex(/^[a-z0-9][a-z0-9-]*[a-z0-9]$/, 'Lowercase letters, numbers, and hyphens only. Must start and end with alphanumeric.')
    .refine((s) => !s.includes('--'), 'Cannot contain consecutive hyphens (--)')
    .refine((s) => !RESERVED_DOMAINS.has(s), 'This domain name is reserved'),
})

type DomainFormValues = z.infer<typeof domainSchema>

const displayNameSchema = z.object({
  displayName: z.string().trim().min(1).max(40),
})

type DisplayNameFormValues = z.infer<typeof displayNameSchema>

export function AccountPage() {
  const { user, setUser } = useAuth()
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const [serverError, setServerError] = useState<string | null>(null)
  const [displayNameError, setDisplayNameError] = useState<string | null>(null)

  // RequireAuth からのリダイレクト時は state に { required: true } が入る
  const isRequired = !!(location.state as { required?: boolean } | null)?.required
  const isChanging = !!user?.domain

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<DomainFormValues>({
    resolver: zodResolver(domainSchema),
    defaultValues: { domain: user?.domain ?? '' },
  })

  const onSubmit = async (data: DomainFormValues) => {
    setServerError(null)
    try {
      const updated = await updateDomain(data.domain)
      setUser({ ...user!, domain: updated.domain })
      toast.success(t('account.domainSaveSuccess'))
      if (isRequired) {
        void navigate('/dashboard', { replace: true })
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setServerError(t('account.domainConflict'))
      } else {
        setServerError(err instanceof ApiError ? err.message : t('account.domainSaveFailed'))
      }
    }
  }

  const {
    register: registerDisplayName,
    handleSubmit: handleSubmitDisplayName,
    formState: { errors: displayNameErrors, isSubmitting: isSubmittingDisplayName, isDirty: isDisplayNameDirty },
  } = useForm<DisplayNameFormValues>({
    resolver: zodResolver(displayNameSchema),
    defaultValues: { displayName: user?.display_name ?? '' },
  })

  const onSubmitDisplayName = async (data: DisplayNameFormValues) => {
    setDisplayNameError(null)
    try {
      const updated = await updateDisplayName(data.displayName)
      setUser({ ...user!, display_name: updated.display_name })
      toast.success(t('account.displayNameSaveSuccess'))
    } catch (err) {
      setDisplayNameError(err instanceof ApiError ? err.message : t('account.displayNameSaveFailed'))
    }
  }

  return (
    <div className="flex-1 overflow-auto">
      <div className="mx-auto max-w-2xl p-6 space-y-8">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('account.title')}</h1>
          <p className="text-sm text-muted-foreground mt-1">{user?.email}</p>
        </div>

        {/* 表示名設定(共有ギャラリーでの公開ハンドル) */}
        <section className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold">{t('account.displayName')}</h2>
            <p className="text-sm text-muted-foreground">{t('account.displayNameDescription')}</p>
          </div>

          <form onSubmit={handleSubmitDisplayName(onSubmitDisplayName)} className="space-y-3">
            {displayNameError && (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {displayNameError}
              </p>
            )}

            <div className="space-y-2">
              <Label htmlFor="displayName">{t('account.displayName')}</Label>
              <Input
                id="displayName"
                placeholder={t('account.displayNamePlaceholder')}
                className="max-w-64"
                {...registerDisplayName('displayName')}
              />
              <p className="text-xs text-muted-foreground">{t('account.displayNameHint')}</p>
              {displayNameErrors.displayName && (
                <p className="text-sm text-destructive">{displayNameErrors.displayName.message}</p>
              )}
            </div>

            <Button type="submit" disabled={isSubmittingDisplayName || !isDisplayNameDirty}>
              {isSubmittingDisplayName ? t('common.saving') : t('common.save')}
            </Button>
          </form>
        </section>

        {/* ドメイン設定 */}
        <section className="space-y-4">
          <div>
            <h2 className="text-lg font-semibold">{t('account.domain')}</h2>
            <p className="text-sm text-muted-foreground">{t('account.domainDescription')}</p>
          </div>

          {/* セットアップ必須バナー */}
          {isRequired && (
            <div className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800">
              <Info className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{t('account.domainRequired')}</span>
            </div>
          )}

          {/* 変更時の警告（セットアップ中は非表示） */}
          {isChanging && !isRequired && (
            <div className="flex items-start gap-2 rounded-md border border-yellow-200 bg-yellow-50 px-3 py-2 text-sm text-yellow-800">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{t('account.domainChangeWarning')}</span>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
            {serverError && (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {serverError}
              </p>
            )}

            <div className="space-y-2">
              <Label htmlFor="domain">{t('account.domain')}</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="domain"
                  placeholder="yourname"
                  className="max-w-48 font-mono"
                  autoFocus={isRequired}
                  {...register('domain')}
                />
                <span className="text-sm text-muted-foreground font-mono">--slug.{MOCK_BASE_DOMAIN}</span>
              </div>
              <p className="text-xs text-muted-foreground">{t('account.domainHint')}</p>
              {errors.domain && (
                <p className="text-sm text-destructive">{errors.domain.message}</p>
              )}
            </div>

            {/* プレビュー */}
            {user?.domain && (
              <p className="text-xs text-muted-foreground">
                {t('account.currentDomain')}:&nbsp;
                <span className="font-mono text-foreground">{user.domain}--slug.{MOCK_BASE_DOMAIN}</span>
              </p>
            )}

            <Button type="submit" disabled={isSubmitting || !isDirty}>
              {isSubmitting ? t('common.saving') : t('common.save')}
            </Button>
          </form>
        </section>
      </div>
    </div>
  )
}
